import React, { useContext, useEffect, useState } from 'react';
import { CardThreeBounce, Documents, SelectField } from '../../../components';
import TextArea from '../../../components/InputField/TextArea';
import { REVIEW_TASK_KIND } from '../../../constants/reviewConstants';
import { useReviewNavigationContext } from '../../../components/ReviewWizard/useReviewNavigation';
import { DialogContext } from '../../../store/context/DialogContext';
import { useTranslation } from '../../../store/context/TranslationContext';
import { doGET, doPUT } from '../../../util/HttpUtil';
import ButtonGroupNew from './BusinessLiteForms/components/ButtonGroupNew';
import { InputLabel } from './components/IndividualLite/UserDetailsForm';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../components/InputField/testId';
import { askKey, documentFieldKey, getActiveAsks, isAnswered, isRemoved } from './askIdentity';
import './ExtraQuestions.scss';

export const FormEndpoints = {
    INDIVIDUAL_LITE: "individual-lite",
    BUSINESS_LITE: "business-lite",
    INDIVIDUAL_PLUS: "individual-lite",
    BUSINESS_PLUS: "business-lite",
    INIDIVIDUAL_PRIME: "individual-lite",
    INDIVIDUAL_PRIME: "individual-lite",
    BUSINESS_PRIME: "business-prime",
    BUSINESS : "business"
}

export const FormEndpointsForEdd = {
    INDIVIDUAL_LITE: "individual-lite",
    BUSINESS_LITE: "business",
    INDIVIDUAL_PLUS: "individual-lite",
    BUSINESS_PLUS: "business",
    INIDIVIDUAL_PRIME: "individual-lite",
    INDIVIDUAL_PRIME: "individual-lite",
    BUSINESS_PRIME: "business",
    BUSINESS: "business"
}

// Story 13630 — stable key used to attach a response to a specific ask.
// New asks match by ask_id; legacy rows fall back to the historical question_id||question key,
// which preserves the pre-existing collapse-by-text behavior for old data.
const matchKey = (ask = {}) => ask?.ask_id ?? `legacy:${ask?.question_id || ask?.question || ""}`;

// Legacy duplicates (same question, no ask_id) historically rendered once. Prefer the actionable
// (unanswered) one, else the latest by askedAt. New asks (unique ask_id) are never collapsed.
const moreRelevantAsk = (a, b) => {
    const aAns = isAnswered(a), bAns = isAnswered(b);
    if (aAns !== bAns) return aAns ? b : a;
    return (b?.askedAt ?? 0) >= (a?.askedAt ?? 0) ? b : a;
};
const dedupeAsksForDisplay = (asks = []) => {
    const byKey = new Map();
    for (const ask of asks) {
        const k = matchKey(ask);
        byKey.set(k, byKey.has(k) ? moreRelevantAsk(byKey.get(k), ask) : ask);
    }
    return [...byKey.values()];
};

const sortByAskedAt = (asks = []) => [...asks]
    .map((item, index) => ({ ...item, __index: index }))
    .sort((a, b) => {
        if (a.askedAt && b.askedAt) return a.askedAt - b.askedAt;
        if (a.askedAt && !b.askedAt) return 1;
        if (!a.askedAt && b.askedAt) return -1;
        return a.__index - b.__index;
    })
    .map(({ __index, ...item }) => item);

const ExtraQuestions = ({ dataLoading, formType, data, asLabel, questionIds = [], formId, onSubmit, isForEDD, testId }) => {
    const [rows, setRows] = useState([]);
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(false);
    const [applicationData, setApplicationData] = useState(data);
    const { showError } = useContext(DialogContext)
    const { t } = useTranslation()
    const reviewNavigation = useReviewNavigationContext();
    const resolvedTestId = resolveAutomationTestId(testId, "extra-questions");
    const endpoint = isForEDD ? FormEndpointsForEdd[formType] : FormEndpoints[formType];

    useEffect(() => {
        if (Object.keys(data ?? {})?.length) {
            setApplicationData(data)
        }
    }, [JSON?.stringify(data)]);

    useEffect(() => {
        setLoading(dataLoading)
    }, [dataLoading])

    // Build one row per ACTIVE ask, keyed by ask_id. Saved-question input type/options come from the
    // verification-ask catalog; the display label always comes from the application-specific ask.question.
    const buildRows = (catalogRows) => {
        const catalogById = (catalogRows ?? []).reduce((acc, c) => {
            if (c?._id != null) acc[c._id] = c;
            return acc;
        }, {});

        const activeAsks = sortByAskedAt(getActiveAsks(applicationData?.asks || []));
        // Collapse legacy duplicates (no ask_id) so a question never renders twice, as the old UI did.
        const displayAsks = dedupeAsksForDisplay(asLabel ? activeAsks : activeAsks.filter((ask) => !isAnswered(ask)));

        const nextRows = displayAsks.map((ask, index) => {
            const catalog = ask?.question_id ? catalogById[ask.question_id] : null;
            const inputType = catalog?.inputType || (ask?.attachment ? "DOCUMENT" : "TEXT");
            return {
                key: askKey(ask, index),
                match: matchKey(ask),
                ask_id: ask?.ask_id,
                question_id: ask?.question_id,
                title: ask?.question || catalog?.title || `Additional question ${index + 1}`,
                remark: ask?.remark || "",
                inputType,
                options: catalog?.options || [],
                documentField: documentFieldKey(ask),
                ask,
            };
        });

        const responsesMap = displayAsks.reduce((acc, ask) => {
            acc[matchKey(ask)] = { ...ask };
            return acc;
        }, {});

        setRows(nextRows);
        setResponses(responsesMap);
    };

    useEffect(() => {
        const fetchExtraQuestionsAndResponses = async () => {
            setLoading(true);
            try {
                if (questionIds.length > 0) {
                    const questionsQuery = questionIds.join(',');
                    const cannedQuesResponse = await doGET(`/api/verification-ask/grid?_id=in[${questionsQuery}]`);
                    if (cannedQuesResponse?.status === 200) {
                        buildRows(cannedQuesResponse.data.rows ?? []);
                    }
                } else {
                    buildRows([]);
                }
            } catch (err) {
                showError('An error occurred while fetching the questions.');
            } finally {
                setLoading(false);
            }
        };

        fetchExtraQuestionsAndResponses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asLabel, JSON.stringify(questionIds), JSON.stringify((applicationData?.asks || []).map((a) => a?.ask_id ?? a?.question))]);

    const handleResponseChange = (key, value) => {
        setResponses(prev => ({
            ...prev,
            [key]: { ...(prev[key]), response: value },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Validate locally first.
            for (const row of rows) {
                const resp = responses[row.match];
                if (!resp?.response) {
                    showError("Please answer all the questions before submitting.");
                    return;
                }
            }

            // Story 13630 — refetch the freshest application and MERGE responses by ask_id rather than
            // replacing asks[]. Removed/missing asks (changed by an agent while this page was open) are
            // skipped, never resurrected, and never block submit.
            const detail = await doGET(`/api/${endpoint}/detail?id=${formId}`);
            const latest = detail?.status === 200 ? (detail.data ?? applicationData) : applicationData;
            const latestAsks = latest?.asks ?? [];

            const now = Date.now();
            const merged = latestAsks.map((ask) => {
                if (isRemoved(ask)) return ask;                  // leave soft-removed untouched
                const resp = responses[matchKey(ask)];
                if (resp?.response == null || resp.response === "") return ask;
                return { ...ask, response: resp.response, repliedAt: ask?.repliedAt ?? now };
            });

            const stillPending = merged.filter((ask) => !isRemoved(ask) && !isAnswered(ask));
            if (stillPending.length > 0) {
                showError("Please answer all the active questions.");
                return;
            }

            const updateResponse = await doPUT(`/api/${endpoint}/update`, { ...latest, status: 1, asks: merged });
            if (updateResponse?.status == 200) {
                await doGET(`/api/${endpoint}/submit?id=${formId}&markFormSubmit=true`)
                await reviewNavigation?.markTasksRespondedByKind?.(REVIEW_TASK_KIND.EXTRA_QUESTION, { quiet: true });
            }
            onSubmit();
        } catch (err) {
            showError(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}><CardThreeBounce /></div>;

    return (
        <div className='py-3' data-testid={resolvedTestId} data-record-id={formId}>
            {
                rows?.length > 0 ?
                    <div className="aq-list" data-testid={buildAutomationChildTestId(resolvedTestId, "question-list")}> {rows.map((row) => {
                        const questionTestId = buildAutomationChildTestId(resolvedTestId, `question-${row.inputType || "unknown"}`);
                        return (
                            <div key={row.key} className="aq-question" data-testid={questionTestId} data-record-id={row.ask_id || row.question_id || row.title}>

                                {row.remark ? (
                                    <p
                                        data-testid={buildAutomationChildTestId(questionTestId, "remark")}
                                        className="aq-remark"
                                    >
                                        {t(row.remark)}
                                    </p>
                                ) : null}

                                {row.inputType === 'TEXT' && (
                                    <TextArea
                                        data-testid={buildAutomationChildTestId(questionTestId, "text-response")}
                                        label={t(row.title)}
                                        id={row.key}
                                        placeholder={t("Your Response to the above question")}
                                        value={responses[row.match]?.response || ''}
                                        onChange={(value) => handleResponseChange(row.match, value)}
                                        disabled={asLabel || isAnswered(row.ask)}
                                    />
                                )}
                                {row.inputType === 'DOCUMENT' && (
                                    <>
                                        <InputLabel label={row.title} required testId={buildAutomationChildTestId(questionTestId, "document-label")} />
                                        <Documents
                                            dataTestId={buildAutomationChildTestId(questionTestId, "document-response")}
                                            asLabel={asLabel}
                                            inline
                                            module={formType}
                                            showExpiry
                                            field={row.documentField}
                                            record_id={applicationData?._id}
                                            onChange={(value) => {
                                                handleResponseChange(row.match, JSON?.stringify(value?.attachment))
                                            }}
                                        />
                                    </>
                                )}

                                {row.inputType === 'DROPDOWN' && (
                                    <SelectField
                                        data-testid={buildAutomationChildTestId(questionTestId, "dropdown-response")}
                                        id={row.key}
                                        className="mb-4"
                                        label={row.title}
                                        placeholder={`Your Response to the above question`}
                                        isDisabled={asLabel || isAnswered(row.ask)}
                                        data={row.options.map(option => ({ label: option, value: option }))}
                                        value={responses[row.match]?.response || ''}
                                        onChange={(selected) => handleResponseChange(row.match, selected?.value)}
                                    />
                                )}
                            </div>
                        )
                    })}</div> : <div className="aq-empty" data-testid={buildAutomationChildTestId(resolvedTestId, "empty")}> Extra Information Asked from user will appear here</div>}

            {!asLabel && <div className='mt-4' data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                <ButtonGroupNew
                    testId={buildAutomationChildTestId(resolvedTestId, "navigation")}
                    primaryActionTestIdSuffix="submit"
                    isDisabled={loading}
                    onNextClick={handleSubmit}
                    step={2}
                    nextText={t("Submit")}
                    asLabel={false}
                    loading={loading}
                    hideBackButton
                />
            </div>}

        </div>
    );
};

export default ExtraQuestions;
